import Phaser from 'phaser';
import { ItemRarity, ITEM_RARITY_COLORS } from '@shared/constants';

/**
 * Configuración de un efecto visual para un item basado en su rarity
 */
export interface ItemEffectConfig {
  color: number; // Color en hexadecimal (ej: 0x00cc66 para verde set)
  alpha: number; // Opacidad (0-1)
  animationSpeed?: number; // Velocidad de animación (opcional, para efectos animados)
  static?: boolean; // Si es true, el efecto es estático (sin animación)
}

/**
 * Configuraciones de efectos por rareza.
 * - COMMON: sin efecto visual (no aparece en este map).
 * - RARE/EPIC: halo animado en color de rareza.
 * - LEGENDARY: halo dorado más vistoso (alpha más alto).
 * - SET: efecto especial con fog/movimiento de fondo.
 */
const RARITY_EFFECT_CONFIGS: Partial<Record<ItemRarity, ItemEffectConfig>> = {
  [ItemRarity.RARE]: {
    color: ITEM_RARITY_COLORS[ItemRarity.RARE],
    alpha: 0.35,
    animationSpeed: 0.002,
    static: false,
  },
  [ItemRarity.EPIC]: {
    color: ITEM_RARITY_COLORS[ItemRarity.EPIC],
    alpha: 0.4,
    animationSpeed: 0.002,
    static: false,
  },
  [ItemRarity.LEGENDARY]: {
    color: ITEM_RARITY_COLORS[ItemRarity.LEGENDARY],
    alpha: 0.5,
    animationSpeed: 0.003,
    static: false,
  },
  [ItemRarity.SET]: {
    color: ITEM_RARITY_COLORS[ItemRarity.SET],
    alpha: 0.35,
    animationSpeed: 0.002,
    static: false,
  },
};

/**
 * Manager para crear y gestionar efectos visuales en items del inventario
 */
export class ItemEffectManager {
  private readonly scene: Phaser.Scene;
  private readonly effects: Map<number, Phaser.GameObjects.Container> = new Map(); // Efectos de rareza
  private readonly enchantEffects: Map<number, Phaser.GameObjects.Container> = new Map(); // Efectos de encantamiento (sobre rareza)
  private readonly enchantMaskGraphics: Map<number, Phaser.GameObjects.Graphics> = new Map(); // Graphics usados para máscaras de encantamiento
  private readonly effectItemIds: Map<number, string> = new Map(); // Track qué itemId tiene cada efecto
  private readonly effectEnchantIds: Map<number, string | null> = new Map(); // Track qué enchantId tiene cada efecto
  private readonly effectRarities: Map<number, ItemRarity> = new Map(); // Track la rareza rolled de cada efecto

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Crea un efecto visual para un slot de inventario.
   * `rolledRarity` es la rareza específica de la instancia (no del template).
   * COMMON no recibe efecto visual.
   */
  createEffect(
    slotIndex: number,
    x: number,
    y: number,
    size: number,
    itemId: string,
    rolledRarity: ItemRarity,
    enchantId?: string | null
  ): void {
    // Limpiar efectos existentes si hay
    this.removeEffect(slotIndex);

    const config = RARITY_EFFECT_CONFIGS[rolledRarity] ?? null;

    // Crear efecto de rareza si existe (COMMON no tiene efecto)
    if (config) {
      const container = this.scene.add.container(x, y);
      container.setDepth(850); // Justo debajo del icono del item (851)
      container.setScrollFactor(0);

      // Asegurar que el efecto no bloquee eventos de pointer
      container.setInteractive(new Phaser.Geom.Rectangle(-size/2, -size/2, size, size), Phaser.Geom.Rectangle.Contains);
      container.disableInteractive();

      if (rolledRarity === ItemRarity.SET) {
        // SET: fondo completo con fog/movimiento
        this.createSetItemEffect(container, size, config);
      } else if (config.static) {
        this.createStaticEffect(container, size, config);
      } else {
        this.createAnimatedEffect(container, size, config);
      }

      this.effects.set(slotIndex, container);
    }

    // Crear efecto de encantamiento si existe (sobre el de rareza)
    if (enchantId) {
      this.createEnchantmentEffect(slotIndex, x, y, size);
    }

    this.effectItemIds.set(slotIndex, itemId);
    this.effectEnchantIds.set(slotIndex, enchantId ?? null);
    this.effectRarities.set(slotIndex, rolledRarity);
  }

  /**
   * Crea un efecto estático (degrade simple)
   */
  private createStaticEffect(
    container: Phaser.GameObjects.Container,
    size: number,
    config: ItemEffectConfig
  ): void {
    // Crear múltiples círculos concéntricos para simular un degrade radial
    // Más abarcativo dentro del slot
    const layers = 4;
    const baseRadius = size * 0.6; // Más grande: 60% del tamaño del slot
    
    for (let i = 0; i < layers; i++) {
      const layerRadius = baseRadius * (0.6 + (i / layers) * 0.4); // Más abarcativo
      const layerAlpha = config.alpha * (1 - i * 0.2);
      const gradient = this.scene.add.circle(0, 0, layerRadius, config.color, layerAlpha);
      gradient.setBlendMode(Phaser.BlendModes.ADD);
      container.add(gradient);
    }

    // Agregar un borde sutil
    const border = this.scene.add.circle(0, 0, baseRadius, 0x000000, 0);
    border.setStrokeStyle(1, config.color, config.alpha * 0.5);
    container.add(border);
  }

  /**
   * Crea un efecto animado (humo/degrade con animación sutil)
   */
  private createAnimatedEffect(
    container: Phaser.GameObjects.Container,
    size: number,
    config: ItemEffectConfig
  ): void {
    // Crear múltiples capas de humo para efecto más rico
    // Aumentar el tamaño para que sea más abarcativo dentro del slot
    const layers = 4;
    const baseRadius = size * 0.6; // Más grande: 60% del tamaño del slot (antes era 50%)

    for (let i = 0; i < layers; i++) {
      const layerAlpha = config.alpha * (1 - i * 0.15); // Cada capa más transparente
      const layerRadius = baseRadius * (0.6 + i * 0.1); // Capas más abarcativas, empezando desde 60% del baseRadius
      
      // Crear círculo con degrade
      const smoke = this.scene.add.circle(0, 0, layerRadius, config.color, layerAlpha);
      smoke.setBlendMode(Phaser.BlendModes.ADD);
      container.add(smoke);

      // Agregar animación sutil de pulsación/opacidad
      const minAlpha = layerAlpha * 0.4;
      const maxAlpha = layerAlpha;
      const delay = i * 200; // Cada capa con un pequeño delay en ms
      
      // Animación de pulsación suave
      this.scene.tweens.add({
        targets: smoke,
        alpha: minAlpha,
        duration: 1800 + i * 300, // Duración diferente para cada capa
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: delay,
      });
    }

    // Agregar un borde sutil animado
    const border = this.scene.add.circle(0, 0, baseRadius, 0x000000, 0);
    border.setStrokeStyle(1, config.color, config.alpha * 0.6);
    container.add(border);

    // Animar el borde también con pulsación más sutil
    this.scene.tweens.add({
      targets: border,
      alpha: config.alpha * 0.3,
      duration: 2000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Crea un efecto especial para items de SET: fog/niebla que llena todo el fondo del slot
   * El efecto se mantiene dentro de los bordes, moviéndose desde los bordes hacia adentro
   */
  private createSetItemEffect(
    container: Phaser.GameObjects.Container,
    size: number,
    config: ItemEffectConfig
  ): void {
    // Capa base estática con tinte sutil que cubre todo el slot
    const baseTint = this.scene.add.rectangle(0, 0, size * 0.98, size * 0.98, config.color, config.alpha * 0.4);
    baseTint.setBlendMode(Phaser.BlendModes.ADD);
    container.add(baseTint);

    // Crear múltiples capas de fog que se mueven desde los bordes hacia adentro
    const fogLayers = 3;
    const maxMovement = size * 0.08; // Movimiento limitado para que no se salga
    const fogSize = size * 0.96; // Tamaño ligeramente menor que el slot
    
    for (let i = 0; i < fogLayers; i++) {
      // Cada capa de fog cubre casi todo el slot pero se mueve dentro de los límites
      const fogAlpha = config.alpha * 0.4 * (1 - i * 0.2);
      const fog = this.scene.add.rectangle(0, 0, fogSize, fogSize, config.color, fogAlpha);
      fog.setBlendMode(Phaser.BlendModes.ADD);
      container.add(fog);

      // Movimiento desde los bordes hacia adentro (movimiento contenido y suave)
      const duration = 5000 + i * 1000; // Velocidad diferente para cada capa
      const delay = i * 600;
      
      // Movimiento horizontal: desde borde hacia centro y viceversa
      const directionX = i % 2 === 0 ? 1 : -1;
      this.scene.tweens.add({
        targets: fog,
        x: { from: -maxMovement * directionX, to: maxMovement * directionX },
        duration: duration,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: delay,
      });

      // Movimiento vertical: desde borde hacia centro y viceversa
      const directionY = i % 3 === 0 ? 1 : -1;
      this.scene.tweens.add({
        targets: fog,
        y: { from: -maxMovement * directionY * 0.8, to: maxMovement * directionY * 0.8 },
        duration: duration * 1.2, // Velocidad ligeramente diferente
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: delay + 300,
      });
    }

    // Agregar capas de resplandor en los bordes que se mueven hacia adentro
    const edgeGlowLayers = 2;
    const edgeGlowSize = size * 0.92; // Más pequeño que el slot para que no se salga
    
    for (let i = 0; i < edgeGlowLayers; i++) {
      const glowAlpha = config.alpha * 0.6 * (1 - i * 0.3);
      const glow = this.scene.add.rectangle(0, 0, edgeGlowSize, edgeGlowSize, config.color, glowAlpha);
      glow.setBlendMode(Phaser.BlendModes.ADD);
      container.add(glow);

      // Escala que pulsa desde los bordes hacia adentro (contenida)
      const glowDuration = 4000 + i * 800;
      const glowDelay = i * 400;
      
      glow.setScale(0.90 + i * 0.05);
      this.scene.tweens.add({
        targets: glow,
        scaleX: { from: 0.85 + i * 0.05, to: 0.95 + i * 0.03 },
        scaleY: { from: 0.85 + i * 0.05, to: 0.95 + i * 0.03 },
        duration: glowDuration,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: glowDelay,
      });

      // Opacidad que varía sutilmente
      this.scene.tweens.add({
        targets: glow,
        alpha: { from: glowAlpha * 0.6, to: glowAlpha },
        duration: glowDuration * 1.1,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: glowDelay + 200,
      });
    }
  }

  /**
   * Crea un efecto visual especial para items con encantamiento
   * Este efecto debe ser más prominente que el de rareza y estar sobre él
   */
  private createEnchantmentEffect(slotIndex: number, x: number, y: number, size: number): void {
    const container = this.scene.add.container(x, y);
    container.setDepth(850.5); // Debajo del icono del item (851), pero sobre el efecto de rareza (850)
    container.setScrollFactor(0);
    
    // Asegurar que el efecto no bloquee eventos de pointer
    container.setInteractive(new Phaser.Geom.Rectangle(-size/2, -size/2, size, size), Phaser.Geom.Rectangle.Contains);
    container.disableInteractive();

    // Crear máscara para limitar el efecto al slot
    // IMPORTANTE: Crear el maskGraphics fuera de la escena para evitar que se renderice
    // y luego ocultarlo inmediatamente después de crear la máscara
    const maskGraphics = this.scene.add.graphics({ x: x, y: y });
    maskGraphics.fillStyle(0xffffff);
    maskGraphics.fillRect(-size/2, -size/2, size, size);
    const mask = maskGraphics.createGeometryMask();
    container.setMask(mask);
    // IMPORTANTE: Ocultar completamente el maskGraphics inmediatamente
    // Si no se oculta correctamente, puede aparecer como un cuadrado blanco en (0,0)
    maskGraphics.setVisible(false);
    maskGraphics.setActive(false);
    maskGraphics.setAlpha(0); // Asegurar que esté completamente invisible
    maskGraphics.setDepth(-1000); // Asegurar que esté muy detrás
    maskGraphics.setScrollFactor(0);
    // Guardar referencia al maskGraphics para destruirlo cuando el container se destruya
    this.enchantMaskGraphics.set(slotIndex, maskGraphics);

    // Color violeta claro para encantamientos
    const enchantColor = 0xaa88ff; // Violeta claro
    const enchantAlpha = 0.25; // 50% de la opacidad original (0.5 -> 0.25)

    // Capa base: resplandor violeta que cubre todo el slot
    const baseGlow = this.scene.add.rectangle(0, 0, size * 0.95, size * 0.95, enchantColor, enchantAlpha * 0.3);
    baseGlow.setBlendMode(Phaser.BlendModes.ADD);
    container.add(baseGlow);

    // Anillo exterior pulsante (destello)
    const outerRing = this.scene.add.circle(0, 0, size * 0.55, enchantColor, 0);
    outerRing.setStrokeStyle(2, enchantColor, enchantAlpha);
    outerRing.setBlendMode(Phaser.BlendModes.ADD);
    container.add(outerRing);

    // Animación de pulso del anillo exterior
    this.scene.tweens.add({
      targets: outerRing,
      scaleX: { from: 0.9, to: 1.1 },
      scaleY: { from: 0.9, to: 1.1 },
      alpha: { from: enchantAlpha * 0.5, to: enchantAlpha },
      duration: 1000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // Partículas/destellos que rotan alrededor del centro
    const sparkCount = 6;
    for (let i = 0; i < sparkCount; i++) {
      const angle = (i / sparkCount) * Math.PI * 2;
      const radius = size * 0.35;
      const sparkX = Math.cos(angle) * radius;
      const sparkY = Math.sin(angle) * radius;

      // Destello pequeño
      const spark = this.scene.add.circle(sparkX, sparkY, size * 0.08, enchantColor, enchantAlpha * 0.8);
      spark.setBlendMode(Phaser.BlendModes.ADD);
      container.add(spark);

      // Animación: pulso de tamaño y opacidad
      this.scene.tweens.add({
        targets: spark,
        alpha: { from: enchantAlpha * 0.4, to: enchantAlpha * 0.9 },
        scaleX: { from: 0.8, to: 1.2 },
        scaleY: { from: 0.8, to: 1.2 },
        duration: 800 + i * 100,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: i * 150,
      });

      // Rotación orbital usando un objeto temporal para almacenar el ángulo
      const sparkData: { angle: number } = { angle: angle };
      this.scene.tweens.add({
        targets: sparkData,
        angle: angle + Math.PI * 2,
        duration: 3000 + i * 200,
        ease: 'Linear',
        repeat: -1,
        onUpdate: () => {
          const newX = Math.cos(sparkData.angle) * radius;
          const newY = Math.sin(sparkData.angle) * radius;
          spark.setPosition(newX, newY);
        },
      });
    }

    // Resplandor central que pulsa
    const centerGlow = this.scene.add.circle(0, 0, size * 0.25, enchantColor, enchantAlpha * 0.6);
    centerGlow.setBlendMode(Phaser.BlendModes.ADD);
    container.add(centerGlow);

    this.scene.tweens.add({
      targets: centerGlow,
      scaleX: { from: 0.8, to: 1.3 },
      scaleY: { from: 0.8, to: 1.3 },
      alpha: { from: enchantAlpha * 0.4, to: enchantAlpha * 0.8 },
      duration: 1200,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // Bordes brillantes pulsantes (4 esquinas)
    const cornerSize = size * 0.15;
    const corners = [
      { x: -size * 0.4, y: -size * 0.4 },
      { x: size * 0.4, y: -size * 0.4 },
      { x: size * 0.4, y: size * 0.4 },
      { x: -size * 0.4, y: size * 0.4 },
    ];

    corners.forEach((corner, index) => {
      const cornerGlow = this.scene.add.rectangle(corner.x, corner.y, cornerSize, cornerSize, enchantColor, enchantAlpha * 0.7);
      cornerGlow.setBlendMode(Phaser.BlendModes.ADD);
      container.add(cornerGlow);

      this.scene.tweens.add({
        targets: cornerGlow,
        alpha: { from: enchantAlpha * 0.3, to: enchantAlpha * 0.9 },
        scaleX: { from: 0.7, to: 1.3 },
        scaleY: { from: 0.7, to: 1.3 },
        duration: 1000 + index * 200,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: index * 250,
      });
    });

    this.enchantEffects.set(slotIndex, container);
  }

  /**
   * Actualiza la posición de un efecto
   */
  updateEffectPosition(slotIndex: number, x: number, y: number): void {
    const effect = this.effects.get(slotIndex);
    if (effect) {
      effect.setPosition(x, y);
    }
    const enchantEffect = this.enchantEffects.get(slotIndex);
    if (enchantEffect) {
      enchantEffect.setPosition(x, y);
    }
  }

  /**
   * Actualiza el efecto de un slot (útil cuando cambia el item, rareza o encantamiento).
   * Solo recrea el efecto si itemId, rolledRarity o enchantId cambiaron, para no
   * reiniciar las animaciones.
   */
  updateEffect(
    slotIndex: number,
    x: number,
    y: number,
    size: number,
    itemId: string | null,
    rolledRarity: ItemRarity,
    enchantId?: string | null
  ): void {
    if (!itemId) {
      this.removeEffect(slotIndex);
      return;
    }

    const existingItemId = this.effectItemIds.get(slotIndex);
    const existingEnchantId = this.effectEnchantIds.get(slotIndex) ?? null;
    const existingRarity = this.effectRarities.get(slotIndex);
    const newEnchantId = enchantId ?? null;

    const itemChanged = existingItemId !== itemId;
    const enchantChanged = existingEnchantId !== newEnchantId;
    const rarityChanged = existingRarity !== rolledRarity;

    if (!itemChanged && !enchantChanged && !rarityChanged && this.effects.has(slotIndex)) {
      this.updateEffectPosition(slotIndex, x, y);
      return;
    }

    this.createEffect(slotIndex, x, y, size, itemId, rolledRarity, enchantId);
  }

  /**
   * Muestra u oculta un efecto
   */
  setEffectVisible(slotIndex: number, visible: boolean): void {
    const effect = this.effects.get(slotIndex);
    if (effect) {
      effect.setVisible(visible);
    }
    const enchantEffect = this.enchantEffects.get(slotIndex);
    if (enchantEffect) {
      enchantEffect.setVisible(visible);
    }
  }

  /**
   * Elimina un efecto de un slot
   */
  removeEffect(slotIndex: number): void {
    const effect = this.effects.get(slotIndex);
    if (effect) {
      effect.destroy();
      this.effects.delete(slotIndex);
    }
    const enchantEffect = this.enchantEffects.get(slotIndex);
    if (enchantEffect) {
      enchantEffect.destroy();
      this.enchantEffects.delete(slotIndex);
    }
    // Destruir el maskGraphics asociado si existe
    const maskGraphics = this.enchantMaskGraphics.get(slotIndex);
    if (maskGraphics) {
      maskGraphics.destroy();
      this.enchantMaskGraphics.delete(slotIndex);
    }
    this.effectItemIds.delete(slotIndex);
    this.effectEnchantIds.delete(slotIndex);
    this.effectRarities.delete(slotIndex);
  }

  /**
   * Elimina todos los efectos
   */
  destroy(): void {
    this.effects.forEach((effect) => effect.destroy());
    this.effects.clear();
    this.enchantEffects.forEach((effect) => effect.destroy());
    this.enchantEffects.clear();
    // Destruir todos los maskGraphics
    this.enchantMaskGraphics.forEach((maskGraphics) => maskGraphics.destroy());
    this.enchantMaskGraphics.clear();
    this.effectItemIds.clear();
    this.effectEnchantIds.clear();
  }
}

